"use client";

import { FilterIcon as FilterIconOutline, SearchIcon, TrashIcon } from "@heroicons/react/outline";
import { FilterIcon as FilterIconSolid } from "@heroicons/react/solid";
import { useCallback, useMemo } from "react";
import { ICellRendererParams, RowClassParams, RowHeightParams } from "ag-grid-community";

import { Button } from "../../Button";
import { Input } from "../../input";
import { ContextMenuVariant } from "../ContextMenu";
import { DropdownMenuVariant } from "../DropdownMenu";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { SimpleTable, SimpleTableColumn } from "../SimpleTable";
import { GenericTablePagination } from "../table-pagination";

import { TableActions } from "./TableActions";
import { BaseData, DataTableProps } from "./types";

export function DataTable<DataT extends BaseData>({
  onRowClick,
  data,
  columns,
  actions,
  isLoading,
  contextMenuItems,
  emptyStateMessage = "No data found",
  action,
  filterContent,
  filterPopoverOpen,
  onFilterPopoverOpenChange,
  activeFilterCount,
  searchBarAddon,
  hasSearch,
  search,
  fullHeight,
  setSearch,
  maxHeight,
  rowHeight,
  objectName,
  height,
  rowsDeletable,
  onDelete,
  searchPlaceholder,
  getDeleteDisabled,
  getDeleteShowIf,
  getDeleteTooltip,
  deleteConfirmSaveVariant = "destructiveOutline",
  deleteConfirmText,
  onRequestDeleteConfirm,
  pagination,
  page = 1,
  perPage = 20,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50],
  enablePagination,
  currentPage,
  pageSize,
  onPaginationChanged,
  licenseKey,
  ...tableProps
}: DataTableProps<DataT>) {
  const requestDeleteConfirmation = useCallback(async () => {
    const message = `Are you sure you want to delete ${objectName}? This action cannot be undone.`;
    if (onRequestDeleteConfirm) {
      return onRequestDeleteConfirm({
        message,
        saveButtonVariant: deleteConfirmSaveVariant,
        confirmText: deleteConfirmText,
      });
    }
    return window.confirm(message);
  }, [objectName, onRequestDeleteConfirm, deleteConfirmSaveVariant, deleteConfirmText]);

  const tableColumns: SimpleTableColumn<DataT>[] = columns.map(column => ({
    suppressMovable: true,
    ...column,
  }));

  if ((actions && actions.length > 0) || rowsDeletable) {
    const hasActions = actions && actions.length > 0;
    const columnActions = rowsDeletable
      ? [
          ...(hasActions ? [...actions, { id: "divider", isDivider: true }] : []),
          {
            id: "delete",
            label: "Delete",
            icon: TrashIcon,
            variant: DropdownMenuVariant.DESTRUCTIVE,
            onClick: async (item?: DataT) => {
              if (item) {
                const isConfirmed = await requestDeleteConfirmation();
                if (isConfirmed) {
                  onDelete?.(item);
                }
              }
            },
            getDisabled: (item?: DataT) => {
              if (!item) {
                return false;
              }
              return getDeleteDisabled?.(item) ?? false;
            },
            showIf: (item?: DataT) => {
              if (!item) {
                return false;
              }
              return getDeleteShowIf?.(item) ?? true;
            },
            tooltipContent: getDeleteTooltip,
          },
        ]
      : actions;

    tableColumns.push({
      header: "",
      width: 60,
      suppressMovable: true,
      minWidth: 60,
      maxWidth: 60,
      sortable: false,
      resizable: false,
      // eslint-disable-next-line react/no-unstable-nested-components
      cellRenderer: (params: ICellRendererParams<DataT>) => (
        <TableActions<DataT> actions={columnActions} item={params.data!} />
      ),
    });
  }

  const contextMenuItemsWithDelete = useMemo(() => {
    const hasContextMenuItems = contextMenuItems && contextMenuItems.length > 0;
    const itemsWithDelete = hasContextMenuItems ? [...contextMenuItems] : [];
    if (rowsDeletable) {
      if (hasContextMenuItems) {
        itemsWithDelete.push({
          id: "divider",
          isDivider: true,
        });
      }
      itemsWithDelete.push({
        id: "delete",
        label: "Delete",
        icon: TrashIcon,
        variant: ContextMenuVariant.DESTRUCTIVE,
        getDisabled: (item?: DataT) => {
          if (!item) {
            return false;
          }
          return getDeleteDisabled?.(item) ?? false;
        },
        onClick: async (item?: DataT) => {
          if (item) {
            const isConfirmed = await requestDeleteConfirmation();
            if (isConfirmed) {
              onDelete?.(item);
            }
          }
        },
        showIf: (item?: DataT) => {
          if (!item) {
            return false;
          }
          return getDeleteShowIf?.(item) ?? true;
        },
        tooltipContent: getDeleteTooltip ? (item?: DataT) => getDeleteTooltip(item) : undefined,
      });
    }

    return itemsWithDelete;
  }, [
    contextMenuItems,
    requestDeleteConfirmation,
    onDelete,
    getDeleteDisabled,
    getDeleteShowIf,
    getDeleteTooltip,
    rowsDeletable,
  ]);

  const hasActionsOrSearch = action || hasSearch || searchBarAddon || filterContent;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getRowHeight = useCallback((params: RowHeightParams<DataT, any>) => {
    const isDetailRow = params.node.detail;
    if (isDetailRow) {
      return 400;
    }
    return 48;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getRowClass = useCallback((params: RowClassParams<DataT, any>) => {
    const isDetailRow = params.node.detail;
    if (isDetailRow) {
      return "!bg-white hover:!bg-white";
    }
  }, []);

  return (
    <div className="flex h-full flex-col">
      {hasActionsOrSearch && (
        <div className="my-2 flex h-[40px] items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative flex min-w-[300px] items-center gap-2">
              {hasSearch && (
                <>
                  <SearchIcon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                  <Input
                    value={search ?? ""}
                    onChange={e => setSearch?.(e.target.value)}
                    placeholder={searchPlaceholder ? `${searchPlaceholder}...` : "Search..."}
                    className="box-border h-10 w-full min-w-[300px] border-0 bg-gray-100 pl-10 pr-4 text-sm shadow-none focus:border-0 focus:ring-0"
                  />
                </>
              )}
              {searchBarAddon}
            </div>
            <div className="flex items-center gap-2">
              {filterContent && (
                <Popover
                  {...(onFilterPopoverOpenChange && {
                    open: filterPopoverOpen,
                    onOpenChange: onFilterPopoverOpenChange,
                  })}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="relative flex-shrink-0 gap-2"
                      aria-label="Filters"
                    >
                      {(activeFilterCount ?? 0) > 0 ? (
                        <FilterIconSolid className="h-3.5 w-3.5" />
                      ) : (
                        <FilterIconOutline className="h-3.5 w-3.5" />
                      )}
                      {(activeFilterCount ?? 0) > 0 && (
                        <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-blue-500 text-[9px] font-medium text-white">
                          {(activeFilterCount ?? 0) > 9 ? "9+" : activeFilterCount}
                        </span>
                      )}
                      Filters
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side="bottom"
                    align="end"
                    className="max-h-[70vh] w-[360px] overflow-y-auto overscroll-contain p-0"
                    sideOffset={6}
                    onWheel={event => event.stopPropagation()}
                  >
                    {filterContent}
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>
          {action}
        </div>
      )}
      <div className="min-h-0 flex-1 overflow-hidden">
        <SimpleTable<DataT>
          data={data}
          columns={tableColumns}
          contextMenuItems={contextMenuItemsWithDelete}
          isLoading={isLoading}
          onRowClick={onRowClick}
          emptyStateMessage={emptyStateMessage}
          height={height}
          maxHeight={maxHeight}
          rowHeight={rowHeight}
          getRowClass={getRowClass}
          getRowHeight={getRowHeight}
          enableFiltering={false}
          enableSorting
          enableResizing
          suppressHeaderMenuButton
          enablePagination={onPaginationChanged ? (enablePagination ?? true) : undefined}
          currentPage={onPaginationChanged ? currentPage : undefined}
          pageSize={onPaginationChanged ? pageSize : undefined}
          onPaginationChanged={onPaginationChanged}
          licenseKey={licenseKey}
          {...tableProps}
        />
      </div>
      {onPageChange && onPageSizeChange && !onPaginationChanged && (
        <div className="mt-4">
          <GenericTablePagination
            pages={pagination?.pages ?? 1}
            hasNext={pagination?.has_next ?? false}
            hasPrev={pagination?.has_prev ?? false}
            page={pagination?.page ?? page}
            pageSize={pagination?.per_page ?? perPage}
            pageSizeOptions={pageSizeOptions}
            setPageIndex={onPageChange}
            setPageSize={(size: number) => onPageSizeChange(size)}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}
